/**
 * Search Filters Configuration
 * Configuration for extended filters and sorting in property search for realtors
 * Story-3: Extended filters and sorting in property search
 */

import { CallStatus } from "@/types/enums";

// Note: ExclusiveType and PublicationStatus are now server-synced via usePropertyOptions
// Use dynamic options from server instead of hardcoded enum values

// Extended search filter fields for realtors
export interface ExtendedSearchFilters {
  // Price filters
  priceTotalMin?: number;
  priceTotalMax?: number;
  pricePerSqmMin?: number;
  pricePerSqmMax?: number;
  priceMode?: "total" | "perSqM";

  // Area filters
  areaMin?: number;
  areaMax?: number;

  // Floor filters
  floorMin?: number;
  floorMax?: number;

  // Realtor-specific filters
  exclusiveType?: string[]; // Server-synced via usePropertyOptions.getExclusiveTypes()
  callStatus?: CallStatus[];
  publicationStatus?: string[]; // Server-synced via usePropertyOptions.getPublicationStatuses()

  // Date range filters
  lastCallFrom?: Date;
  lastCallTo?: Date;
  updatedFrom?: Date;
  updatedTo?: Date;

  // Assignment filters
  creatorId?: string;
  assigneeId?: string;
}

// Form field configuration for filter panel
export interface FilterFieldConfig {
  key: keyof ExtendedSearchFilters;
  type: "range" | "select" | "multiselect" | "daterange";
  label: string;
  width: number; // Grid column width (1-12)
  required?: boolean;
  placeholder?: string;
  icon?: string;
  options?: Array<{ value: string; label: string; icon?: string }>;
  validation?: {
    min?: number;
    max?: number;
  };
}

// Sorting configuration
export interface SortConfig {
  key: string;
  label: string;
  direction: "asc" | "desc";
  default?: boolean;
}

// Main search filters configuration
export const SEARCH_FILTERS_CONFIG: FilterFieldConfig[] = [
  // Price Range (Total)
  {
    key: "priceTotalMin",
    type: "range",
    label: "search.filters.price.total",
    width: 6,
    placeholder: "search.filters.price.min",
    validation: { min: 0 },
  },
  {
    key: "priceTotalMax",
    type: "range",
    label: "search.filters.price.total",
    width: 6,
    placeholder: "search.filters.price.max",
    validation: { min: 0 },
  },

  // Price Range (Per SqM)
  {
    key: "pricePerSqmMin",
    type: "range",
    label: "search.filters.price.perSqm",
    width: 6,
    placeholder: "search.filters.price.min",
    validation: { min: 0 },
  },
  {
    key: "pricePerSqmMax",
    type: "range",
    label: "search.filters.price.perSqm",
    width: 6,
    placeholder: "search.filters.price.max",
    validation: { min: 0 },
  },

  // Area Range
  {
    key: "areaMin",
    type: "range",
    label: "search.filters.area",
    width: 6,
    placeholder: "search.filters.area.min",
    validation: { min: 0 },
  },
  {
    key: "areaMax",
    type: "range",
    label: "search.filters.area",
    width: 6,
    placeholder: "search.filters.area.max",
    validation: { min: 0 },
  },

  // Floor Range
  {
    key: "floorMin",
    type: "range",
    label: "search.filters.floor",
    width: 6,
    placeholder: "search.filters.floor.min",
    validation: { min: 0 },
  },
  {
    key: "floorMax",
    type: "range",
    label: "search.filters.floor",
    width: 6,
    placeholder: "search.filters.floor.max",
    validation: { min: 0 },
  },

  // Exclusive Type
  {
    key: "exclusiveType",
    type: "select",
    label: "search.filters.exclusiveType",
    width: 3,
    icon: "exclusive",
    // Note: Options are dynamically populated from usePropertyOptions.getExclusiveTypes()
    // This is a fallback configuration - real options come from server
    options: [],
  },

  // Call Status
  {
    key: "callStatus",
    type: "select",
    label: "search.filters.callStatus",
    width: 3,
    icon: "phone",
    options: [
      { value: CallStatus.NOT_CALLED, label: "callStatus.notCalled" },
      { value: CallStatus.NO_ANSWER, label: "callStatus.noAnswer" },
      { value: CallStatus.INTERESTED, label: "callStatus.interested" },
      { value: CallStatus.NOT_INTERESTED, label: "callStatus.notInterested" },
      { value: CallStatus.WRONG_NUMBER, label: "callStatus.wrongNumber" },
    ],
  },

  // Last Call Date Range
  {
    key: "lastCallFrom",
    type: "daterange",
    label: "search.filters.lastCall",
    width: 6,
    placeholder: "search.filters.date.from",
  },
  {
    key: "lastCallTo",
    type: "daterange",
    label: "search.filters.lastCall",
    width: 6,
    placeholder: "search.filters.date.to",
  },
];

// Sorting options configuration
export const SORT_OPTIONS: SortConfig[] = [
  {
    key: "createdAt",
    label: "sort.dateAdded.newest",
    direction: "desc",
    default: true,
  },
  {
    key: "createdAt",
    label: "sort.dateAdded.oldest",
    direction: "asc",
  },
  {
    key: "updatedAt",
    label: "sort.dateUpdated.newest",
    direction: "desc",
  },
  {
    key: "updatedAt",
    label: "sort.dateUpdated.oldest",
    direction: "asc",
  },
  {
    key: "lastCallDate",
    label: "sort.lastCall.newest",
    direction: "desc",
  },
  {
    key: "lastCallDate",
    label: "sort.lastCall.oldest",
    direction: "asc",
  },
  {
    key: "price",
    label: "sort.price.highest",
    direction: "desc",
  },
  {
    key: "price",
    label: "sort.price.lowest",
    direction: "asc",
  },
  {
    key: "area",
    label: "sort.area.largest",
    direction: "desc",
  },
  {
    key: "area",
    label: "sort.area.smallest",
    direction: "asc",
  },
  {
    key: "floor",
    label: "sort.floor.highest",
    direction: "desc",
  },
  {
    key: "floor",
    label: "sort.floor.lowest",
    direction: "asc",
  },
];

// Default sort option
export const DEFAULT_SORT =
  SORT_OPTIONS.find((sort) => sort.default) || SORT_OPTIONS[0];

// Exclusive type icons mapping (now using string keys)
export const EXCLUSIVE_TYPE_ICONS: Record<string, string> = {
  my: "exclusive-my",
  agency: "exclusive-agency",
  other_agency: "exclusive-other",
  owner: "exclusive-owner",
};

// Utility functions for filter configuration
export const getFilterFieldByKey = (
  key: keyof ExtendedSearchFilters,
): FilterFieldConfig | undefined => {
  return SEARCH_FILTERS_CONFIG.find((field) => field.key === key);
};

export const getFilterFieldsByType = (
  type: FilterFieldConfig["type"],
): FilterFieldConfig[] => {
  return SEARCH_FILTERS_CONFIG.filter((field) => field.type === type);
};

export const getSortOptionByKey = (
  key: string,
  direction: "asc" | "desc",
): SortConfig | undefined => {
  return SORT_OPTIONS.find(
    (sort) => sort.key === key && sort.direction === direction,
  );
};

// Table column configuration for search results
export interface TableColumnConfig {
  key: string;
  label: string;
  width?: string;
  sortable?: boolean;
  type?: "text" | "image" | "price" | "date" | "badge" | "actions";
}

export const SEARCH_TABLE_COLUMNS: TableColumnConfig[] = [
  {
    key: "id",
    label: "table.columns.id",
    width: "80px",
    sortable: true,
    type: "text",
  },
  {
    key: "photo",
    label: "table.columns.photo",
    width: "80px",
    type: "image",
  },
  {
    key: "address",
    label: "table.columns.address",
    sortable: true,
    type: "text",
  },
  {
    key: "price",
    label: "table.columns.price",
    width: "120px",
    sortable: true,
    type: "price",
  },
  {
    key: "mainInfo.area",
    label: "table.columns.area",
    width: "80px",
    sortable: true,
    type: "text",
  },
  {
    key: "mainInfo.rooms",
    label: "table.columns.rooms",
    width: "80px",
    sortable: true,
    type: "text",
  },
  {
    key: "mainInfo.floor",
    label: "table.columns.floor",
    width: "80px",
    sortable: true,
    type: "text",
  },
  {
    key: "updatedAt",
    label: "table.columns.updated",
    width: "100px",
    sortable: true,
    type: "date",
  },
  {
    key: "lastCall",
    label: "table.columns.lastCall",
    width: "100px",
    sortable: true,
    type: "date",
  },
  {
    key: "exclusive",
    label: "table.columns.exclusive",
    width: "80px",
    type: "badge",
  },
  {
    key: "actions",
    label: "table.columns.actions",
    width: "60px",
    type: "actions",
  },
];

// Pagination configuration
export const PAGINATION_CONFIG = {
  defaultPageSize: 20,
  pageSizeOptions: [20, 50, 100],
  maxVisiblePages: 7,
} as const;

// Reset filters configuration
export const RESET_FILTERS_CONFIG = {
  excludeFields: ["page", "limit", "sort", "dealType"] as const,
  resetLabel: "filters.reset",
  resetIcon: "refresh",
} as const;
